/**
 * Parent-order-scoped sibling selection state for linked-order modals.
 * @module dataAccumulator/useSiblingOrderSelection
 */
import { useCallback, useEffect, useState } from "react";

/** Return value for {@link useSiblingOrderSelection}. */
export interface UseSiblingOrderSelectionResult {
  /** Linked sibling order ids currently selected in expandable tables. */
  selectedSiblingOrderIds: string[];
  /** Replaces the full selected sibling order id list. */
  setSelectedSiblingOrderIds: (orderIds: string[]) => void;
  /** When true, sibling-order checkboxes cannot be toggled. */
  isSiblingOrderSelectionDisabled: boolean;
  /** Enables or disables sibling-order checkbox interaction. */
  setIsSiblingOrderSelectionDisabled: (disabled: boolean) => void;
}

/**
 * Tracks linked sibling checkbox selection for the active parent order.
 *
 * Resets selection and the disabled lock whenever the parent order id changes.
 *
 * @param parentOrderId - Current parent PA order id from general order details
 * @returns Selection state and setters scoped to the parent order
 */
export const useSiblingOrderSelection = (
  parentOrderId: string | undefined,
): UseSiblingOrderSelectionResult => {
  const [selectedSiblingOrderIds, setSelectedSiblingOrderIdsState] = useState<
    string[]
  >([]);
  const [isSiblingOrderSelectionDisabled, setIsSiblingOrderSelectionDisabled] =
    useState(false);

  useEffect(() => {
    setSelectedSiblingOrderIdsState([]);
    setIsSiblingOrderSelectionDisabled(false);
  }, [parentOrderId]);

  const setSelectedSiblingOrderIds = useCallback((orderIds: string[]) => {
    setSelectedSiblingOrderIdsState(orderIds);
  }, []);

  return {
    selectedSiblingOrderIds,
    setSelectedSiblingOrderIds,
    isSiblingOrderSelectionDisabled,
    setIsSiblingOrderSelectionDisabled,
  };
};
